import { EventPageType } from '@graphql/generated-base';
import type { EventPageVisibility, PageKind } from '@graphql/generated-base';
import { isEventPageVisible } from '@shared/utils/pageVisibility';
import { isUserGeneratedMigratedSquatter, toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import type { FeatureSet } from './featureSet.types';
import { FeatureSetType } from './featureSet.types';

type FeatureSetPageInfo = Maybe<Readonly<{ disabled: boolean }>>;

type FeatureSetEvent = Readonly<{
  welcomePage?: FeatureSetPageInfo;
  registryPage?: FeatureSetPageInfo;
  rsvpPage?: FeatureSetPageInfo;
  pages: ReadonlyArray<Readonly<{ type: EventPageType; pageSlug: string; pageKind?: Maybe<PageKind>; visibility: EventPageVisibility }>>;
}>;

/**
 * Which FeatureSet areas (Website, Registry, Rsvp) an event has enabled. Shared by `useFeatureSet`
 * and the server middleware that must predict where `useGuestSiteRedirection` sends a guest.
 */
export const deriveFeatureSet = (event: FeatureSetEvent): FeatureSet => {
  // A missing welcome PageInfo (null) must count as website-disabled, not enabled — otherwise a
  // feature-only event with no welcome row keeps Website enabled (so useGuestSiteRedirection never
  // redirects) while useDefaultPage has no default page to render, stranding the guest on a blank
  // root route.
  // A missing PageInfo (null) means the feature is not present — treat it as disabled, not enabled.
  // Otherwise an event with absent welcome + registry + rsvp rows would report { website: false,
  // registry: true } and useGuestSiteRedirection would send guests to a /registry that isn't there.
  const nativeWelcomePageEnabled = !!event.welcomePage && !event.welcomePage.disabled;
  const registryPageEnabled = !!event.registryPage && !event.registryPage.disabled;
  const rsvpPageEnabled = !!event.rsvpPage && !event.rsvpPage.disabled;

  // Website is enabled when the native Welcome page is on, OR when any genuine website custom
  // page is visible. Without the custom-page fallback, events whose only website content is a
  // non-Welcome custom page get redirected to /find even though they have content.
  //
  // The migrated registry page is type=custom but is its own feature (with a dedicated /registry
  // landing), not website content. Counting it here would enable the Website feature for an event
  // whose welcome page is disabled and whose only custom page is the migrated registry — stranding
  // guests on the hidden welcome page instead of redirecting them to /registry. A user-generated
  // page that merely squats the 'registrylinks' slug is real website content, so it still counts.
  const hasVisibleCustomPage = event.pages.some(page => {
    if (page.type !== EventPageType.custom || !isEventPageVisible(page.visibility)) {
      return false;
    }
    const isMigratedRegistry = toCanonicalMigratedSlug(page.pageSlug) === 'registrylinks' && !isUserGeneratedMigratedSquatter(page);
    return !isMigratedRegistry;
  });
  const websiteFeatureEnabled = nativeWelcomePageEnabled || hasVisibleCustomPage;

  return {
    [FeatureSetType.Website]: websiteFeatureEnabled,
    [FeatureSetType.Registry]: registryPageEnabled,
    [FeatureSetType.Rsvp]: rsvpPageEnabled,
  };
};
